import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const hero = "https://images.unsplash.com/photo-1474302770737-173ee21bab63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwamV0JTIwc3Vuc2V0fGVufDB8fHx8MTc4NDcwMzczNnww&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.4 + i * 0.12, duration: 1.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

function MaskLine({ children, i = 0 }) {
  return (
    <span className="mask-wrap">
      <motion.span
        className="block"
        variants={lineVariants}
        custom={i}
        initial="hidden"
        animate="visible"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 800], [0, 200]);
  const scale = useTransform(scrollY, [0, 800], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax bg */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 w-full h-[120%]"
      >
        <img
          src={hero}
          alt="Private jet at sunset"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1626]/55 via-[#0A1626]/60 to-[#0A1626]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,#0A1626_85%)]"></div>
      </motion.div>

      {/* Top meta */}
      <div className="relative z-10 pt-32 max-w-[1440px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.9 }}
          className="flex items-center gap-4 text-[#C8A97E]"
        >
          <div className="hairline w-16"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Advisory · Asset Trading · Sourcing · Transactions</span>
        </motion.div>
      </div>

      {/* Headline */}
      <div className="relative z-10 mt-10 max-w-[1440px] mx-auto px-6 md:px-10">
        <h1 className="font-serif font-light tracking-[-0.03em] text-white leading-[0.95] text-[46px] sm:text-[72px] lg:text-[104px] xl:text-[128px]">
          <MaskLine i={0}>Strategic aviation</MaskLine>
          <br/>
          <MaskLine i={1}>
            <span className="italic text-[#C8A97E]">solutions</span> built
          </MaskLine>
          <br/>
          <MaskLine i={2}>on intelligence.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="mt-10 max-w-2xl text-white/85 text-[16px] md:text-[18px] leading-relaxed"
        >
          An aviation advisory, asset trading, aircraft sourcing and transaction partner — combining market intelligence, technical expertise and global relationships to deliver exceptional results on every mandate.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/services"
            data-testid="hero-cta-explore"
            className="btn-gold px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2"
          >
            Explore Services <ArrowUpRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-consultation"
            className="btn-ghost-dark px-8 py-4 text-[12px] uppercase tracking-[0.25em] font-mono inline-flex items-center gap-2"
          >
            Schedule a Consultation
          </Link>
        </motion.div>
      </div>

      {/* Bottom ticker */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/10 backdrop-blur-xl bg-[#0A1626]/70"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-5 grid grid-cols-3 gap-6">
          {[
            ["Global Reach", "6 Regions"],
            ["Aircraft Categories", "12 Segments"],
            ["Client Retention", "94%"],
          ].map(([k,v], i) => (
            <div key={i} className="flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">{k}</span>
              <span className="mt-1 font-serif text-white text-[20px] md:text-[24px]">{v}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
